import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import useUpdateOrderStatus from '../../../../../../hooks/orders/useUpdateOrderStatus';
import ArrowReturnIcon from '../../../../../svg-icons/ArrowReturnIcon';
import OrderValidatedIcon from '../../../../../svg-icons/OrderValidatedIcon';
import OrderWaitingSCIcon from '../../../../../svg-icons/OrderWaitingSCIcon';
import LoadingButton from '../../../../../utils/common/LoadingButton';
import { getFormattedNote } from '../utils';
import { setSnackBar } from '../../../../../../states/app/staticSlice';
import { useDispatch } from 'react-redux';

const UpdateOrderStatus = ({
  ticketId,
  order,
  profile,
  handleShowComponent,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [formattedNote, setFormattedNote] = useState('');
  const [stateToLock, setStateToLock] = useState(false);
  const { updateStatus } = useUpdateOrderStatus({ order });
  const dispatch = useDispatch();
  const currentStatus = order?.status?.code;
  const canUpdateStatus = ['VALIDATED', 'WAITING_SC'].includes(currentStatus);

  const getNextStatus = () => {
    return currentStatus === 'VALIDATED'
      ? { code: 'WAITING_SC', name: 'En attente SC' }
      : { code: 'VALIDATED', name: 'Validée' };
  };

  const getButtonLabel = () => {
    return currentStatus === 'VALIDATED'
      ? 'Passer en attente SC'
      : 'Valider la commande';
  };

  const getDescription = () => {
    return currentStatus === 'WAITING_SC'
      ? `En validant la commande, elle passera du statut "En attente SC" au statut "Validée".`
      : `En passant la commande en attente SC, elle passera du statut "Validée" au statut "En attente SC".`;
  };

  const handleNoteContentChange = (event) => {
    setNoteContent(event.target.value);
  };

  const handleStatusUpdate = async () => {
    setIsLoading(true);
    try {
      const { code: orderStatus, name } = getNextStatus();
      const userId = profile?._id;

      const updatedOrder = await updateStatus({
        statusCode: orderStatus,
        userId,
        note: formattedNote,
        stateToLock,
        ticketId,
      });

      if (!updatedOrder) {
        console.log('updatedOrder', updatedOrder);
        throw new Error('Failed to update order status');
      }

      setIsDialogOpen(false);
      handleShowComponent(null);
      const messageError = `La commande ${order?.reference} a bien été mise à jour au statut ${name}.`;
      dispatch(
        setSnackBar({ open: true, message: messageError, color: 'success' })
      );
    } catch (error) {
      console.error('Failed to update order status:', error);
      const messageError =
        error?.message ||
        `Une erreur est survenue lors de la mise à jour du statut de la commande ${order?.reference}.`;
      dispatch(
        setSnackBar({ open: true, message: messageError, color: 'error' })
      );
      setIsDialogOpen(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = () => {
    const { name } = getNextStatus();

    const formattedData = getFormattedNote(
      profile?.firstName + ' ' + profile?.lastName,
      noteContent,
      name,
      'status',
      stateToLock
    );
    setFormattedNote(formattedData);
    setIsDialogOpen(true);
  };

  return (
    <>
      <Card
        elevation={5}
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          width: '100%',
          padding: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {order?.status?.code === 'VALIDATED' ? (
              <OrderWaitingSCIcon fontSize="small" />
            ) : (
              <OrderValidatedIcon fontSize="small" />
            )}
            <Typography color="primary">
              Mise à jour du statut de la commande
            </Typography>
          </Box>
          <Button
            onClick={() => handleShowComponent(null)}
            variant="outlined"
            size="small"
            sx={{ position: 'absolute', top: 16, left: 16 }}
          >
            <ArrowReturnIcon fontSize="small" />
            <span style={{ marginLeft: 4, fontSize: '0.9em' }}>Retour</span>
          </Button>
        </Box>
      </Card>
      <Box
        sx={{
          mt: 2,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          margin: '10rem 0',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            width: '100%',
          }}
        >
          {/*TODO: remove order?.type?.code !== 'ORDER_SAV' to authorize status lock on SAV orders*/}
          {currentStatus === 'WAITING_SC' &&
            order?.type?.code !== 'ORDER_SAV' && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {/* TODO: disable switch when bov2 serve is stateToLockData */}
                <Switch
                  value={stateToLock}
                  disabled={order?.isStateToLock}
                  onChange={() => setStateToLock((v) => !v)}
                />
                <Typography sx={{ margin: '0 1em' }}>
                  Bloquer le status de la commande
                </Typography>
                <Tooltip
                  placement="top"
                  title={`À la réception d'un prochain message, la commande ne passera pas automatiquement en attentte SC et restera au statut Validée. Cette action est irreversible.`}
                >
                  <HelpOutlineIcon sx={{ fontSize: '1em' }} />
                </Tooltip>
              </Box>
            )}
          <Typography>{getDescription()}</Typography>
          <TextField
            id="status-note"
            label="Commentaire (optionnel)"
            multiline
            rows={3}
            value={noteContent}
            onChange={handleNoteContentChange}
            fullWidth
            sx={{ mt: 2 }}
          />
          <Button
            variant="outlined"
            color="primary"
            onClick={handleConfirm}
            sx={{ width: '50%', mt: 2 }}
            disabled={!canUpdateStatus || isLoading}
          >
            {getButtonLabel()}
          </Button>
        </Box>
      </Box>

      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir changer le statut de la commande ?
          </DialogContentText>
          {stateToLock && (
            <DialogContentText sx={{ display: 'flex', alignItems: 'center' }}>
              Le statut de la commande sera bloqué
              <Tooltip
                placement="top"
                title={`À la réception d'un prochain message, la commande ne passera pas automatiquement en attentte SC et restera au statut Validée`}
              >
                <HelpOutlineIcon />
              </Tooltip>
            </DialogContentText>
          )}
          {formattedNote && (
            <>
              <Typography sx={{ mt: 2 }}>
                <strong>Note:</strong>
              </Typography>
              <Typography
                sx={{
                  fontStyle: 'italic',
                  padding: 2,
                  border: 1,
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  borderColor: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: 1,
                  mt: 1,
                }}
              >
                {formattedNote}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsDialogOpen(false)}
            color="primary"
            variant="outlined"
          >
            Annuler
          </Button>
          <LoadingButton
            isLoading={isLoading}
            onClick={handleStatusUpdate}
            color="primary"
            variant="contained"
          >
            Confirmer
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UpdateOrderStatus;
